/**
 * Reset Password Page Component
 *
 * Password reset page with email input for requesting reset link.
 * Uses the shell-appropriate shared authentication layout.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loading01 } from '@untitled-ui/icons-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { AuthRouteLayout } from '@/components/auth/auth-route-layout';
import { WEB_BACK_LINK_CLASS, getAuthPageChrome } from '@/components/auth/auth-page-chrome';
import { AuthTextField } from '@/components/auth/auth-text-field';
import { FormField, HookForm } from '@/components/base/form/hook-form';
import { entryBackLinkClass } from '@/components/brand/entry-styles';
import { Button } from '@/components/base/buttons/button';
import { isDesktopPlatform } from '@/lib/desktop/bridge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getSupabaseFunctionError } from '@/lib/supabase-utils';

function createFormSchema(invalidEmail: string) {
  return z.object({
    email: z.string().email({ message: invalidEmail }),
  });
}

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

const LOGIN_PATH = '/auth/login';

const ResetPassword = () => {
  const { t } = useTranslation('auth');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const desktop = isDesktopPlatform();
  const chrome = getAuthPageChrome();

  const formSchema = useMemo(() => createFormSchema(t('login.invalidEmail')), [t]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleResetPassword = async (values: FormData) => {
    setIsLoading(true);

    try {
      const result = await supabase.functions.invoke('reset-password', {
        body: {
          email: values.email,
        },
      });

      if (result.error) {
        const errorMessage = await getSupabaseFunctionError(result.error);
        toast({
          title: t('resetPassword.errorTitle'),
          description: errorMessage,
          variant: 'destructive',
        });
      } else {
        const data = result.data as { success: boolean; message?: string } | null;
        if (data?.success) {
          setEmailSent(true);
          toast({
            title: t('resetPassword.checkEmailTitle'),
            description: data.message || t('resetPassword.defaultSentMessage'),
          });
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast({
        title: t('resetPassword.errorTitle'),
        description: t('resetPassword.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthRouteLayout>
      {/* On the desktop entry canvas the text link is seated on the content
          column's left edge; see `entryBackLinkClass`. */}
      <Button
        className={desktop ? entryBackLinkClass : WEB_BACK_LINK_CLASS}
        color="tertiary"
        size="md"
        onClick={() => {
          Promise.resolve(navigate(LOGIN_PATH)).catch(() => null);
        }}
      >
        <ArrowLeft aria-hidden="true" className="mr-2 size-4" />
        {t('resetPassword.back')}
      </Button>

      {emailSent ? (
        <div className="flex flex-col gap-4">
          <div className="mb-2">
            <h1 className={chrome.heading}>{t('resetPassword.sentHeading')}</h1>
            <p className={chrome.description}>
              {t('resetPassword.sentDescription')}{' '}
              <span className={chrome.emphasis}>{form.getValues('email')}</span>
            </p>
          </div>

          <Button
            className={chrome.primaryButton}
            size="md"
            type="button"
            onClick={() => setEmailSent(false)}
          >
            {t('resetPassword.sendAgain')}
          </Button>
        </div>
      ) : (
        <HookForm
          className="flex flex-col gap-4"
          form={form}
          onSubmit={(e) => {
            form
              .handleSubmit(handleResetPassword)(e)
              .catch(() => {
                /* handled by react-hook-form */
              });
          }}
        >
          <div className="mb-2">
            <h1 className={chrome.heading}>{t('resetPassword.heading')}</h1>
            <p className={chrome.description}>{t('resetPassword.description')}</p>
          </div>

          <FormField control={form.control} name="email">
            {({ field, fieldState }) => (
              <AuthTextField
                {...field}
                autoComplete="email"
                hint={fieldState.error?.message}
                inputClassName={chrome.input}
                isInvalid={fieldState.invalid}
                label={t('login.emailLabel')}
                labelClassName={chrome.label}
                placeholder={t('login.emailPlaceholder')}
                type="email"
              />
            )}
          </FormField>

          <Button className={chrome.primaryButton} isDisabled={isLoading} size="md" type="submit">
            {isLoading ? (
              <>
                <Loading01 aria-hidden="true" className="mr-2 size-4 animate-spin" />
                {t('resetPassword.sending')}
              </>
            ) : (
              t('resetPassword.sendLink')
            )}
          </Button>
        </HookForm>
      )}

      <div className="mt-6 text-center">
        <span className={chrome.mutedText}>{t('signInPrompt.remember')} </span>
        <Button
          className={chrome.link}
          color="link-color"
          size="md"
          onClick={() => {
            Promise.resolve(navigate(LOGIN_PATH)).catch(() => null);
          }}
        >
          {t('signInPrompt.signIn')}
        </Button>
      </div>
    </AuthRouteLayout>
  );
};

export default ResetPassword;
